/**
 * COPIAR AL PORTAPAPELES SIN QUE REVIENTE.
 *
 * `navigator.clipboard.writeText()` RECHAZA la promesa en varios casos normales, y si nadie la
 * atrapa queda como un rechazo sin manejar que va a parar a Sentry:
 *
 *   NotAllowedError: Failed to execute 'writeText' on 'Clipboard': Document is not focused.
 *
 * Pasa cuando entre el clic y la escritura el documento perdió el foco — otra ventana encima, las
 * herramientas del navegador abiertas, un clic que además abrió algo. Reportado el 2026-09-15 en
 * /dept/supplements/queue/First Estimate, copiando la dirección de un claim.
 *
 * De los siete lugares que copiaban, cuatro no atajaban nada. En vez de poner un try en cada uno
 * —y que el próximo nazca sin él— se copia por acá:
 *
 *  · si la API moderna falla, se prueba el método viejo (un textarea invisible + execCommand), que
 *    justamente no exige foco del documento y funciona donde el otro se niega;
 *  · nunca rechaza: devuelve true o false y quien llama decide qué mostrar.
 *
 * Lo del método viejo no es adorno: `navigator.clipboard` tampoco existe fuera de un contexto
 * seguro, y ahí el fallback es lo único que queda.
 */
export async function copiarAlPortapapeles(texto: string): Promise<boolean> {
  const valor = String(texto ?? '');
  if (!valor) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(valor);
      return true;
    }
  } catch { /* sigue por el camino viejo */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = valor;
    // Fuera de la vista pero enfocable: si `display:none` no se puede seleccionar y no copia nada.
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, valor.length); // iOS ignora select() a secas
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
